import { IProductCatalogLookupBulkDto, IProductCatalogLookupDto } from "../../products/models/ProductCatalog";
import { getCatalogLookupsBulk, filterOutLookupsByParentId, getLeadtimeProductGroups, getAllProductCodesForLeadTimeGroup, filterOutProductCode } from "../../products/api/ProductCatalogApi";
import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { exportProductCatalog } from "../redux/ProductCatalogActions";

type Props = {
  productType?: string;
  productCode?: string;
}

interface IPapPair {
  key: string;
  description: string;
  options: { option: string; values: string[] }[];
}

const ProductCatalogWidget = ({ productType, productCode }: Props) => {
  const [papPairs, setPapPairs] = useState<IPapPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [selectedProductType, setSelectedProductType] = useState<string>('');
  const dispatch = useDispatch();
  
  const { actionsLoading } = useSelector(
    (state: any) => ({
      actionsLoading: state.productCatalog.actionsLoading,
    }),
    shallowEqual
  );

  // Use prop if provided, otherwise use selected value
  const activeProductType = productType || selectedProductType;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const setProductCodes = (productCodes: IProductCatalogLookupDto[], hive: IProductCatalogLookupBulkDto[]) => {
    const data = productCodes.map((code: IProductCatalogLookupDto) => {
        const optionKeys = filterOutLookupsByParentId(hive, code.id);
        const options = optionKeys.map((optionKey: IProductCatalogLookupDto) => {
          const optionValues = filterOutLookupsByParentId(hive, optionKey.id);
          return {
            option: optionKey.key,
            values: optionValues.map((optionValue: IProductCatalogLookupDto) => optionValue.key)
          };
        });
        return { key: code.key, description: code.description, options: options };
      });
      setPapPairs(data);
  }

  // Load product types when no productType or productCode is provided
  useEffect(() => {
    if (!productType && !productCode) {
      getLeadtimeProductGroups()
        .then(dto => {
          const productTypeValues = dto.map(group => group.physicalProductGroup);
          const uniqueProductTypes = Array.from(new Set(productTypeValues));
          setProductTypes(uniqueProductTypes.sort((a, b) => a.localeCompare(b)));
        })
        .catch(error => {
          console.error('Failed to load product types:', error);
        });
    }
  }, [productType, productCode]);

  useEffect(() => {
    if (!activeProductType && !productCode) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getCatalogLookupsBulk()
      .then((hive: IProductCatalogLookupBulkDto[]) => {
          if (activeProductType) {
              getAllProductCodesForLeadTimeGroup(activeProductType, hive).then(productCodes => {
                  setProductCodes(productCodes, hive);
                  setLoading(false);
              });
          } else if (productCode) {
              const productCodes = filterOutProductCode(productCode, hive);
              setProductCodes(productCodes, hive);
              setLoading(false);
          }
      })
      .catch(error => {
        console.error('Failed to load catalog data:', error);
        setLoading(false);
      });
  }, [activeProductType, productCode]);

  const filteredPapPairs = papPairs.filter((dto) => {
    const searchLower = searchText.toLowerCase();
    return (
      dto.key.toLowerCase().includes(searchLower) ||
      dto.description.toLowerCase().includes(searchLower)
    );
  });

  const handleExportAsCsv = () => {
    if (!activeProductType) return;
    
    dispatch(exportProductCatalog(activeProductType) as any);
  };

  const handleProductTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProductType(event.target.value);
  };

  if (loading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  return (
    <>
      {!productCode && <div className="mb-5">
        <div className="d-flex align-items-center">
          {!productType && (
            <select
              className="form-control mr-5"
              style={{ maxWidth: '300px' }}
              value={selectedProductType}
              onChange={handleProductTypeChange}
            >
              <option value="">Select Product Type</option>
              {productTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}
          <div className="input-group search-input-group mr-5" style={{ maxWidth: '400px' }}>
            <input
              type='text'
              className='form-control'
              placeholder='Search by Article code or description'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <div className="input-group-append">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setSearchText('')}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            )}
          </div>
          {activeProductType && (
            <button
              className="btn btn-primary-theme border-3 fw-bold mr-5 mb-sm-5 mb-md-0"
              onClick={handleExportAsCsv}
              disabled={actionsLoading}
            >
              <i className="fa fa-download mr-2"></i>
              {actionsLoading ? 'Downloading...' : 'Download as CSV'}
            </button>
          )}
        </div>
      </div>}
      <div className="table-responsive">
        <table className="table table-head-custom table-vertical-center">
          <thead>
            <tr>
              <th className="pl-7" style={{ minWidth: '150px' }}>Article Code</th>
              <th style={{ minWidth: '200px' }}>Description</th>
              <th style={{ minWidth: '400px' }}>Options & Values</th>
            </tr>
          </thead>
          <tbody>
            {filteredPapPairs.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-5">
                  {activeProductType ? 'No products found' : 'Please select a product type'}
                </td>
              </tr>
            ) : (
              filteredPapPairs.map((dto, index) => (
                <tr key={index}>
                  <td className="pl-7">
                    <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                      {dto.key}
                    </span>
                  </td>
                  <td>
                    <span className="text-muted font-weight-bold">
                      {dto.description}
                    </span>
                  </td>
                  <td>
                    {dto.options
                      .filter((option) => option.option !== 'Pages')
                      .map((option, optIndex) => (
                        <div key={optIndex} className="mb-3">
                          <span className="font-weight-bold text-primary">
                            {option.option}:
                            <i 
                              className="fa fa-copy ml-2 cursor-pointer"
                              onClick={() => copyToClipboard(option.option)}
                              title="Click to copy"
                              style={{ cursor: 'pointer' }}
                            ></i>
                          </span>
                          <div className="d-flex flex-wrap mt-1">
                            {option.values.filter(value => value !== '').map((value, valIndex) => (
                              <span
                                key={valIndex}
                                className="badge badge-light-info mr-2 mb-2"
                              >
                                {value}
                                <i 
                                  className="fa fa-copy ml-1 cursor-pointer"
                                  onClick={() => copyToClipboard(value)}
                                  title="Click to copy"
                                  style={{ cursor: 'pointer' }}
                                ></i>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export { ProductCatalogWidget };
